/**
 * SquadTvDisplay — full-screen "Stadium board" for office TV displays:
 * a compact branding top bar above two dense, continuously auto-scrolling
 * columns (arena-scoreboard style), showing as many members at once as fit.
 */

import React, { useMemo } from 'react';
import { Pressable, View } from 'react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { useSquadTvExit } from '../../../src/worldcup/hooks/useSquadTvExit';
import {
  SQUAD_TV_MODE_COPY,
  SQUAD_TV_MODE_TEST_IDS,
} from '../../../src/worldcup/constants/squadTvMode.constants';
import { splitLeaderboardIntoColumns } from '../../../src/worldcup/helpers/squadTvStadium.helper';
import { SquadTvExitHint } from '../SquadTvExitHint/SquadTvExitHint';
import { SquadTvTopBar } from './SquadTvTopBar';
import { SquadTvColumn } from './SquadTvColumn';
import { getSquadTvDisplayStyles } from './SquadTvDisplay.styles';
import type { LeaderboardEntry, WorldCupGroup } from '../../../src/worldcup/types/worldcup.types';
import type { WorldCupFuturesOptions } from '../../../src/worldcup/helpers/worldcupPicksData.helper';

interface SquadTvDisplayProps {
  group: WorldCupGroup;
  top3: LeaderboardEntry[];
  rest: LeaderboardEntry[];
  onExitTv: () => void;
  /** Reserved for future per-row picks; the Stadium board does not render futures. */
  futuresOptions?: WorldCupFuturesOptions | null;
  isLeaderboardLoading?: boolean;
  autoScrollEnabled?: boolean;
}

export const SquadTvDisplay: React.FC<SquadTvDisplayProps> = ({
  group,
  top3,
  rest,
  onExitTv,
  isLeaderboardLoading = false,
  autoScrollEnabled = true,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getSquadTvDisplayStyles(theme), [theme]);

  const { left, right } = useMemo(
    () => splitLeaderboardIntoColumns([...top3, ...rest]),
    [top3, rest],
  );

  const { handleBannerPress, showExitHint } = useSquadTvExit({
    enabled: true,
    onExit: onExitTv,
  });

  const columnsScrollEnabled = autoScrollEnabled && !isLeaderboardLoading;

  return (
    <View style={styles.root}>
      <Pressable
        testID={SQUAD_TV_MODE_TEST_IDS.bannerPressTarget}
        accessibilityRole="button"
        accessibilityLabel={SQUAD_TV_MODE_COPY.EXIT_TV_BANNER_A11Y}
        onPress={handleBannerPress}
        style={styles.topBarPressTarget}
      >
        <SquadTvTopBar group={group} />
        <SquadTvExitHint visible={showExitHint} />
      </Pressable>
      <View style={styles.field}>
        <SquadTvColumn
          entries={left}
          autoScrollEnabled={columnsScrollEnabled}
          testID={SQUAD_TV_MODE_TEST_IDS.columnLeft}
        />
        <SquadTvColumn
          entries={right}
          autoScrollEnabled={columnsScrollEnabled}
          showDivider
          testID={SQUAD_TV_MODE_TEST_IDS.columnRight}
        />
      </View>
    </View>
  );
};
